import React from "react";
import { connect } from "react-redux";

import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { faExpand } from "@fortawesome/pro-solid-svg-icons/faExpand";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { faThumbtack } from "@fortawesome/pro-solid-svg-icons/faThumbtack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { FormattedMessage } from "react-intl";

import CumulDashboard from "@mapmycustomers/shared/types/cumul/CumulDashboard";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import { CumulDashboardViewMode } from "@app/scene/reports/enums/CumulDashboardViewMode";
import { getCumulDashboardList } from "@app/store/cumul/selectors";
import { RootState } from "@app/store/rootReducer";
import { MAX_PINNED_DASHBOARDS } from "@app/util/consts";

import styles from "./DashboardHeader.module.scss";

interface OwnProps {
  className?: string;
  cumulDashboardList?: Array<CumulDashboard>;
  dashboardId: CumulDashboard["id"];
  hasEditAccess?: boolean;
  isDashboardPinned: boolean;
  isExportRunning?: boolean;
  onChangeMode: (mode: CumulDashboardViewMode) => void;
  onExport?: () => void;
  onFullScreen?: () => void;
  onTogglePinedDashboard: () => void;
  pinnedDashboardIds?: CumulDashboard["id"][];
  showFullScreenOption?: boolean;
  viewMode?: CumulDashboardViewMode;
}

interface Props extends OwnProps {
  canEditDashboard?: boolean;
}

const DashboardHeader: React.FC<Props> = ({
  canEditDashboard,
  className,
  isDashboardPinned,
  isExportRunning,
  onChangeMode,
  onExport,
  onFullScreen,
  onTogglePinedDashboard,
  pinnedDashboardIds,
  showFullScreenOption,
  viewMode,
}) => {
  const canPin = !pinnedDashboardIds || pinnedDashboardIds.length < MAX_PINNED_DASHBOARDS;

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.dashboardHeader}>
        <div>
          {isDashboardPinned ? (
            <Tooltip
              placement="top"
              title={
                <FormattedMessage
                  id="reports.cumul.dashboard.pinned.button.tooltip.unpin"
                  defaultMessage="Unpin from Menu"
                  description="Pinned to Menu button tooltip"
                />
              }
            >
              <Button onClick={onTogglePinedDashboard} type="filled">
                <FormattedMessage
                  id="reports.cumul.dashboard.pinned.button.showPinned"
                  defaultMessage="{icon} Pinned to Menu"
                  description="Pinned to Menu button text"
                  values={{ icon: <FontAwesomeIcon icon={faThumbtack} /> }}
                />
              </Button>
            </Tooltip>
          ) : (
            <Tooltip
              placement="top"
              title={
                canPin ? (
                  <FormattedMessage
                    id="reports.cumul.dashboard.notPinned.button.tooltip.pinToMenu"
                    defaultMessage="Pin to Menu"
                    description="Not Pinned to Menu button tooltip"
                  />
                ) : (
                  <FormattedMessage
                    id="reports.cumul.dashboard.pinnedDashboard.limit.exceed.tooltip.disabled"
                    defaultMessage="Only 5 pinned dashboards permitted"
                    description="Not Pinned to Menu button tooltip"
                  />
                )
              }
            >
              <Button
                className={styles.pinAction}
                disabled={!canPin}
                onClick={onTogglePinedDashboard}
                type="filled"
              >
                <FormattedMessage
                  id="reports.cumul.dashboard.notPinned.button.notPinned"
                  defaultMessage="Not Pinned"
                  description="Not Pinned to Menu button text"
                />
              </Button>
            </Tooltip>
          )}
        </div>
        <div className={styles.rightMenu}>
          <Button className={cn({ [styles.loader]: isExportRunning })} onClick={onExport}>
            <FormattedMessage
              id="reports.cumul.dashboard.export.button"
              defaultMessage="{icon} Export"
              description="Export dashboard button text"
              values={{
                icon: isExportRunning ? (
                  <LoadingSpinner mini />
                ) : (
                  <FontAwesomeIcon icon={faDownload} />
                ),
              }}
            />
          </Button>
          {showFullScreenOption && (
            <Button onClick={onFullScreen} type="link">
              <FormattedMessage
                id="reports.cumul.dashboard.fullScreen.button.fullScreen"
                defaultMessage="{icon} Full Screen"
                description="Full Screen view of dashboard button text"
                values={{ icon: <FontAwesomeIcon icon={faExpand} /> }}
              />
            </Button>
          )}
          {canEditDashboard ? (
            viewMode === CumulDashboardViewMode.VIEW ? (
              <Button
                onClick={() => onChangeMode(CumulDashboardViewMode.EDIT_LIMITED)}
                type="primary"
              >
                <FormattedMessage
                  id="reports.cumul.dashboard.editDashboard.button"
                  defaultMessage="{icon} Edit Dashboard"
                  description="Edit dashboard button text"
                  values={{ icon: <FontAwesomeIcon icon={faPen} /> }}
                />
              </Button>
            ) : (
              <Button onClick={() => onChangeMode(CumulDashboardViewMode.VIEW)}>
                <FormattedMessage
                  id="reports.cumul.dashboard.backToView.button"
                  defaultMessage="Back to View"
                  description="Back to view option button text"
                />
              </Button>
            )
          ) : undefined}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const cumulDashboardList = getCumulDashboardList(state);
  const dashboard = cumulDashboardList.find(({ id }) => id === ownProps.dashboardId);
  const isDashboardEditable = dashboard?.userAccessRight
    ? dashboard!.userAccessRight.flagModify || dashboard!.userAccessRight.flagOwn
    : false;
  return {
    canEditDashboard: ownProps.hasEditAccess && isDashboardEditable,
  };
};

export default connect(mapStateToProps)(DashboardHeader);
